import { Box } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { JSX } from "react";

import type { DraftStatus, Platform } from "../types.js";
import { PLATFORM_LABELS } from "../types.js";

/**
 * The marketing status-pill family, extracted from `marketing/shared.tsx`.
 * Each tint is built from a theme colour via `alpha` so the pills re-skin with
 * the palette.
 */

/** Tiny status-pill atom shared by every marketing pill below. A soft tinted
 * rounded chip. */
function Pill({
  tint,
  uppercase,
  fontSize,
  children,
}: {
  tint: (t: Theme) => string;
  uppercase?: boolean;
  fontSize?: number;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        borderRadius: 999,
        px: 1,
        py: 0.25,
        fontSize: fontSize ?? 11,
        fontWeight: 500,
        bgcolor: (t) => alpha(tint(t), 0.16),
        color: (t) => tint(t),
        ...(uppercase ? { textTransform: "uppercase", letterSpacing: "0.04em" } : {}),
      }}
    >
      {children}
    </Box>
  );
}

export function PlatformPill({ platform }: { platform: Platform | string }): JSX.Element {
  // Map each platform onto a theme accent so the pills stay on-palette.
  // Several share an accent; the label keeps them distinguishable.
  const tint = (t: Theme): string => {
    switch (platform) {
      case "linkedin_post":
      case "facebook_post":
        return t.palette.info.main;
      case "twitter_thread":
        return t.palette.primary.light;
      case "blog_post":
        return t.palette.system.docs;
      case "press_release":
        return t.palette.warning.main;
      case "video_script":
      case "youtube_short_script":
      case "reddit_post":
        return t.palette.error.main;
      case "instagram_caption":
        return t.palette.system.marketing;
      case "email":
        return t.palette.success.main;
      default:
        return t.palette.text.secondary;
    }
  };
  const label = (PLATFORM_LABELS as Record<string, string>)[platform] ?? platform;
  return <Pill tint={tint}>{label}</Pill>;
}

export function ScheduleStatusPill({ status }: { status: string }): JSX.Element {
  const tint = (t: Theme): string => {
    switch (status) {
      case "published":
        return t.palette.success.main;
      case "publishing":
        return t.palette.info.main;
      case "queued":
        return t.palette.warning.main;
      case "safe_mode":
      case "canceled":
      case "skipped":
        return t.palette.text.secondary;
      default:
        return t.palette.error.main;
    }
  };
  return <Pill tint={tint}>{status.replace(/_/g, " ")}</Pill>;
}

export function ThoughtPill({ type }: { type: string }): JSX.Element {
  return (
    <Pill tint={(t) => thoughtTint(t, type)} uppercase fontSize={10}>
      {type}
    </Pill>
  );
}

/** Theme tint for an agent thought type. Shared by ThoughtPill + the
 *  StatusPage thinking-feed rows so both stay on the same palette. */
export function thoughtTint(t: Theme, type: string): string {
  switch (type) {
    case "plan":
    case "decide":
      return t.palette.primary.light;
    case "search":
      return t.palette.info.main;
    case "generate":
      return t.palette.primary.main;
    case "critique":
      return t.palette.warning.main;
    case "finalize":
      return t.palette.success.main;
    case "error":
      return t.palette.error.main;
    case "observe":
    default:
      return t.palette.text.secondary;
  }
}

export function StatusPill({ status }: { status: DraftStatus | string }): JSX.Element {
  const tint = (t: Theme): string => {
    switch (status) {
      case "approved":
        return t.palette.success.main;
      case "rejected":
        return t.palette.error.main;
      case "in_review":
        return t.palette.warning.main;
      case "published_externally":
        return t.palette.system.docs;
      default:
        return t.palette.text.secondary;
    }
  };
  return <Pill tint={tint}>{status.replace(/_/g, " ")}</Pill>;
}
